import { HttpErrorResponse } from '@angular/common/http';

interface ProblemDetailsLike {
  detail?: string;
  message?: string;
  title?: string;
  errors?: Record<string, string[] | string>;
}

export async function getDocumentActionErrorMessage(
  action: 'upload' | 'download' | 'delete',
  error: unknown
): Promise<string> {
  const prefix = `Failed to ${action} document`;
  const { status, reason } = await extractApiError(error);

  if (status === 0) {
    return `${prefix}: network error. Please check your connection and try again.`;
  }

  if (reason) {
    return `${prefix}: ${reason}`;
  }

  return `${prefix}: an unexpected error occurred.`;
}

async function extractApiError(error: unknown): Promise<{ status: number | null; reason: string | null }> {
  if (!(error instanceof HttpErrorResponse)) {
    return { status: null, reason: null };
  }

  const payload = await normalizePayload(error.error);
  const reason = getReasonFromPayload(payload);

  if (reason) {
    return { status: error.status, reason };
  }

  if (error.status === 401) {
    return { status: error.status, reason: 'your session has expired. Please sign in again.' };
  }

  if (error.status === 403) {
    return { status: error.status, reason: 'you do not have permission to perform this action.' };
  }

  const message = error.message?.trim();
  return { status: error.status, reason: message || null };
}

async function normalizePayload(payload: unknown): Promise<ProblemDetailsLike | string | null> {
  if (payload instanceof Blob) {
    const text = (await payload.text()).trim();
    if (!text) {
      return null;
    }

    try {
      return JSON.parse(text) as ProblemDetailsLike;
    } catch {
      return text;
    }
  }

  if (typeof payload === 'string') {
    const trimmed = payload.trim();
    if (!trimmed) {
      return null;
    }

    try {
      return JSON.parse(trimmed) as ProblemDetailsLike;
    } catch {
      return trimmed;
    }
  }

  if (payload && typeof payload === 'object') {
    return payload as ProblemDetailsLike;
  }

  return null;
}

function getReasonFromPayload(payload: ProblemDetailsLike | string | null): string | null {
  if (!payload) {
    return null;
  }

  if (typeof payload === 'string') {
    return payload;
  }

  const validationErrors = flattenValidationErrors(payload.errors);
  return payload.detail
    ?? payload.message
    ?? validationErrors
    ?? (payload.title && payload.title !== 'Forbidden' && payload.title !== 'Request failed' && payload.title !== 'Server error'
      ? payload.title
      : null);
}

function flattenValidationErrors(errors?: Record<string, string[] | string>): string | null {
  if (!errors) {
    return null;
  }

  for (const value of Object.values(errors)) {
    if (Array.isArray(value) && value.length > 0) {
      return value[0];
    }

    if (typeof value === 'string' && value.trim()) {
      return value.trim();
    }
  }

  return null;
}
